import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import howIEatService from '../../services/howIEatService'
import { HowIEatWeekProgress, HowIEatAchievement } from '../../types/howIEat'
import { useTranslation } from '../../hooks/use-i18n'

const dayLabel = (dateStr: string) => {
  const d = new Date(dateStr + 'T12:00:00')
  return d.toLocaleDateString(undefined, { weekday: 'short' })
}

const HowIEatStatsTab: React.FC = () => {
  const { t } = useTranslation()
  const [data, setData] = useState<HowIEatWeekProgress | null>(null)
  const [achievements, setAchievements] = useState<HowIEatAchievement[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    Promise.all([howIEatService.getWeekProgress(), howIEatService.getAchievements()])
      .then(([week, ach]) => {
        setData(week)
        setAchievements(ach)
      })
      .catch(() => toast.error(t('toast.progressLoadFailed')))
      .finally(() => setIsLoading(false))
  }, [t])

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
      </div>
    )
  }

  const maxCal = Math.max(data.goals.calories, ...data.days.map((d) => d.totals.calories), 1)
  const weightPoints = data.weightTrend.filter((p) => p.weightKg != null) as Array<{ date: string; weightKg: number }>
  const minW = weightPoints.length ? Math.min(...weightPoints.map((p) => p.weightKg)) - 1 : 0
  const maxW = weightPoints.length ? Math.max(...weightPoints.map((p) => p.weightKg)) + 1 : 100
  const unlockedCount = achievements.filter((a) => a.unlocked).length

  return (
    <div>
      <p className="text-white/50 text-sm mb-6">
        {t('howIEat.progress.dateRange', {
          startDate: data.startDate,
          endDate: data.endDate,
          loggedDays: String(data.loggedDays),
        })}
      </p>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-5 mb-4">
        <h2 className="text-white font-semibold mb-4">{t('howIEat.progress.dailyCalories')}</h2>
        <div className="flex items-end justify-between gap-2 h-36">
          {data.days.map((day) => {
            const h = Math.max(4, Math.round((day.totals.calories / maxCal) * 100))
            return (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex items-end justify-center" style={{ height: 100 }}>
                  <div
                    className={`w-full max-w-[28px] rounded-t-md ${day.hasLog ? 'bg-emerald-500' : 'bg-white/10'}`}
                    style={{ height: `${h}%` }}
                    title={`${day.totals.calories} cal`}
                  />
                </div>
                <span className="text-white/50 text-[10px]">{dayLabel(day.date)}</span>
              </div>
            )
          })}
        </div>
        <p className="text-white/50 text-xs mt-3 text-center">
          {t('howIEat.progress.avgCalories', { avg: String(data.averages.calories), goal: String(data.goals.calories) })}
        </p>
      </div>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-5 mb-4">
        <h2 className="text-white font-semibold mb-4">{t('howIEat.progress.macroBreakdown')}</h2>
        <div className="space-y-3">
          {[
            { label: t('howIEat.progress.protein'), pct: data.macroBreakdown.proteinPct, g: data.macroBreakdown.proteinG, color: 'bg-blue-500' },
            { label: t('common.carbs'), pct: data.macroBreakdown.carbsPct, g: data.macroBreakdown.carbsG, color: 'bg-amber-500' },
            { label: t('common.fats'), pct: data.macroBreakdown.fatsPct, g: data.macroBreakdown.fatsG, color: 'bg-rose-500' },
          ].map((m) => (
            <div key={m.label}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-white/70">{m.label}</span>
                <span className="text-white">{t('howIEat.progress.macroValue', { grams: String(m.g), pct: String(m.pct) })}</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className={`h-full ${m.color} rounded-full`} style={{ width: `${m.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {weightPoints.length > 0 && (
        <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-5 mb-4">
          <h2 className="text-white font-semibold mb-4">{t('howIEat.progress.weightTrend')}</h2>
          <svg viewBox="0 0 300 120" className="w-full h-32">
            <polyline
              fill="none"
              stroke="#10b981"
              strokeWidth="2"
              points={data.weightTrend
                .map((p, i) => {
                  const x = 20 + (i / 6) * 260
                  const y = p.weightKg != null ? 100 - ((p.weightKg - minW) / (maxW - minW || 1)) * 80 : null
                  return y != null ? `${x},${y}` : null
                })
                .filter(Boolean)
                .join(' ')}
            />
          </svg>
        </div>
      )}

      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-white font-semibold">{t('howIEat.progress.achievements')}</h2>
          <span className="text-emerald-400 text-sm font-medium">
            {unlockedCount}/{achievements.length}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {achievements.map((a) => (
            <div
              key={a.key}
              className={`p-3 rounded-xl border ${
                a.unlocked ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-white/10 bg-white/5 opacity-60'
              }`}
            >
              <p className="text-lg">{a.icon}</p>
              <p className="text-white text-sm font-semibold mt-1">{a.title}</p>
              <p className="text-white/50 text-xs mt-0.5">{a.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default HowIEatStatsTab
